import Box from '@mui/material/Box'
import React, { useMemo } from 'react'
import { Else, If, Then, When } from 'react-if'

import { MAPS_ACCEPTED, MAPS_DISCARDED } from '../../../Queries/Contests'
import { navigateToEmptyHash } from '../../../Scenes/navigators'
import Button from '../../../Theme/Button'
import CircularProgress from '../../../Theme/CircularProgress'
import FAIcon from '../../../Theme/FAIcon'
import { FA_ICONS } from '../../../Theme/font-awesome-icon-constants'
import { getLeftTimeFromMinutes } from '../../../utils/date-utils'

const ButtonSpinner = () => (
  <CircularProgress
    color="inherit"
    size="16px"
    sx={{ ml: '16px' }}
  />
)

export const StartMapQualificationButton = ({ isLoading, onClick, ...rest }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    <Button
      variant="contained"
      color="primary"
      sx={{ gap: '16px' }}
      onClick={() => !isLoading && onClick()}
      {...rest}
    >
      Start qualifying this map
      <If condition={isLoading}>
        <Then>
          <ButtonSpinner />
        </Then>
        <Else>
          <FAIcon
            iconClassName={FA_ICONS.CHECK_SLOT}
            iconFontSize={16}
          />
        </Else>
      </If>
    </Button>
  </Box>
)

const LockInfoBlock = ({
  fullWidth = true,
  icon = '',
  text = '',
  buttonText = '',
  buttonColor = 'secondary',
  isLoading,
  onClick,
}) => (
  <Box
    sx={(theme) => ({
      width: fullWidth ? '100%' : 'auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 16px',
      background: 'rgba(1, 163, 210, 0.05)',
      borderRadius: '6px',
      color: theme.palette.text.tertiary,
    })}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <FAIcon iconClassName={icon} />
      <Box sx={{ marginLeft: '12px' }}>{text}</Box>
    </Box>

    <When condition={!!onClick && !!buttonText}>
      <Button
        color={buttonColor}
        variant="outlined"
        onClick={onClick}
      >
        {buttonText}
        <When condition={isLoading}>
          <ButtonSpinner />
        </When>
      </Button>
    </When>
  </Box>
)

export const ContestQualificationMapControls = ({
  resetMap,
  resetMapIsLoading,
  discardQuery,
  acceptQuery,
  acceptDisabled = true,
  isMapLockedByMe,
  lockedTimeLeft,
  lastUpdatedByUsername,
  isMapUnchecked,
  isMapDiscarded,
  isMapAccepted,
}) => {
  const { mutate: onDiscard, isLoading: isDiscardLoading } = discardQuery
  const { mutate: onAccept, isLoading: isAcceptLoading } = acceptQuery
  const timeLeft = useMemo(() => getLeftTimeFromMinutes(lockedTimeLeft), [lockedTimeLeft])

  const handleDiscardButton = () => {
    !isDiscardLoading &&
      onDiscard(
        {},
        {
          onSuccess: () => {
            navigateToEmptyHash()
          },
        },
      )
  }

  const handleAcceptButton = () => {
    !isAcceptLoading &&
      onAccept(
        {},
        {
          onSuccess: () => {
            navigateToEmptyHash()
          },
        },
      )
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <When condition={isMapUnchecked}>
        <If condition={!isMapLockedByMe}>
          <Then>
            <LockInfoBlock
              icon={FA_ICONS.INFO}
              text={`${lastUpdatedByUsername} has already started to qualify this map ${timeLeft} left`}
              buttonText="Close"
              onClick={() => navigateToEmptyHash()}
            />
          </Then>
          <Else>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDiscardButton}
            >
              Discard map
              <When condition={isDiscardLoading}>
                <ButtonSpinner />
              </When>
            </Button>

            <LockInfoBlock
              fullWidth={false}
              icon={FA_ICONS.INFO}
              text={`Complete this qualification within ${timeLeft}`}
            />

            <Button
              variant="contained"
              color="primary"
              disabled={acceptDisabled}
              sx={{ gap: '16px' }}
              onClick={handleAcceptButton}
            >
              Accept map
              <If condition={!isAcceptLoading}>
                <Then>
                  <FAIcon
                    iconClassName={FA_ICONS.CHECK}
                    iconFontSize={14}
                  />
                </Then>
                <Else>
                  <ButtonSpinner />
                </Else>
              </If>
            </Button>
          </Else>
        </If>
      </When>

      <When condition={isMapAccepted}>
        <LockInfoBlock
          icon={FA_ICONS.CHECK}
          text={`Accepted by ${lastUpdatedByUsername}`}
          buttonText="Return map to unchecked"
          buttonColor="error"
          isLoading={resetMapIsLoading}
          onClick={() => resetMap({ type: MAPS_ACCEPTED })}
        />
      </When>

      <When condition={isMapDiscarded}>
        <LockInfoBlock
          icon={FA_ICONS.CLOSE}
          text={`Discarded by ${lastUpdatedByUsername}`}
          buttonText="Restore map"
          buttonColor="primary"
          isLoading={resetMapIsLoading}
          onClick={() => resetMap({ type: MAPS_DISCARDED })}
        />
      </When>
    </Box>
  )
}
